import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { doc, getDoc, collection, getDocs, query, where, orderBy, limit, startAfter, DocumentSnapshot } from 'firebase/firestore';
import { FIREBASE_APP, FIREBASE_FIRESTORE } from '../../Firebase';
import { BatchCacheService } from './BatchCacheService';

// Schema types
export interface SchemaField {
  type: 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object' | 'reference';
  required?: boolean;
  arrayItemType?: SchemaField;
  objectSchema?: Record<string, SchemaField>;
  referenceCollection?: string;
  description?: string;
}

export interface CollectionSchema {
  fields: Record<string, SchemaField>;
  version: number;
  createdAt?: any;
  updatedAt?: any;
}

// Types
export interface Collection {
  id: string;
  name: string;
  schema?: CollectionSchema;
}

export interface Organization {
  id: string;
  name: string;
  collections?: Collection[];
  roles?: string[];
  createdAt?: any;
  updatedAt?: any;
}

export interface CloudFunctionResponse {
  success: boolean;
  message?: string;
  data?: any;
  searchTerm?: string;
  totalResults?: number;
}

export interface UserPermissions {
  permissions: string[];     // Global permissions like APP_ADMIN
  orgPermissions: string[];  // Organization permissions like org123_admin
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface BatchDocument {
  id: string;
  orgId: string;
  batch: number;
  data: Record<string, any>;
  createdAt: any;
  updatedAt: any;
  sizeKB: number;
}

export interface PaginatedResult<T> {
  documents: T[];
  totalCount: number;
  hasMore: boolean;
  currentBatch: number;
  currentPage?: number;        // NEW: current page number
  totalPages?: number;         // NEW: total pages available
  loadedBatches?: number[];    // NEW: track which batches are loaded
  nextBatch?: number;          // Keep existing for backward compatibility
}

export interface SearchResult {
  documents: any[];
  totalResults: number;
  searchTerm: string;
}

export interface ApiTokenResponse {
  token: string;
  expiresAt: Date;
  userEmail: string;
  orgId: string;
}

// Initialize Firebase Functions with europe-west2 region
const functions = getFunctions(FIREBASE_APP, 'europe-west2');

// Cloud Functions
const firebaseWriteFn = httpsCallable(functions, 'firebaseWrite');
const firebaseUpdateFn = httpsCallable(functions, 'firebaseUpdate');
const firebaseDeleteFn = httpsCallable(functions, 'firebaseDelete');
const firebaseReadFn = httpsCallable(functions, 'firebaseRead');
const getUserPermissionsFn = httpsCallable(functions, 'getUserPermissions');
const setOrganizationPermissionsFn = httpsCallable(functions, 'setOrganizationPermissions');
const getOrganizationPermissionsFn = httpsCallable(functions, 'getOrganizationPermissions');
const getCollectionSchemaFn = httpsCallable(functions, 'getCollectionSchema');
const validateDocumentAgainstSchemaFn = httpsCallable(functions, 'validateDocumentAgainstSchema');
const searchDocumentsFn = httpsCallable(functions, 'searchDocuments');
const createApiTokenFn = httpsCallable(functions, 'createApiToken');

export class DataService {
  // ========== BATCH-OPTIMIZED READ OPERATIONS ==========

  /**
   * Get the latest batch number for a collection
   */
  static async getLatestBatchNumber(orgId: string, collectionId: string): Promise<number | null> {
    try {
      const batchCollectionId = `${orgId}_${collectionId}_batches`;
      console.log(`🔥 Firestore READ: collection "${batchCollectionId}" - querying latest batch with orderBy(batch, desc), limit(1)`);
      const batchCollection = collection(FIREBASE_FIRESTORE, batchCollectionId);
      
      const q = query(
        batchCollection,
        orderBy('batch', 'desc'),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const latestBatchDoc = querySnapshot.docs[0];
      const batchData = latestBatchDoc.data() as BatchDocument;
      return batchData.batch;
    } catch (error) {
      console.error('Error getting latest batch number:', error);
      throw new Error('Failed to get latest batch number');
    }
  }

  /**
   * Get documents from specific batch(es) - optimized for pagination
   */
  static async getBatchDocuments(
    orgId: string,
    collectionId: string,
    batchNumbers: number[]
  ): Promise<any[]> {
    try {
      const batchCollectionId = `${orgId}_${collectionId}_batches`;
      const batchCollection = collection(FIREBASE_FIRESTORE, batchCollectionId);
      
      // Get all specified batches
      const batchPromises = batchNumbers.map(async (batchNumber) => {
        console.log(`🔥 Firestore READ: collection "${batchCollectionId}" - querying batch ${batchNumber} with where(batch, ==, ${batchNumber}), limit(1)`);
        const q = query(
          batchCollection,
          where('batch', '==', batchNumber),
          limit(1)
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const batchDoc = querySnapshot.docs[0];
          const batchData = batchDoc.data() as BatchDocument;
          return batchData.data || {};
        }
        return {};
      });
      
      const batchResults = await Promise.all(batchPromises);
      
      // Combine all documents from all batches
      const allDocuments: any[] = [];
      batchResults.forEach(batchData => {
        Object.entries(batchData).forEach(([docId, docData]) => {
          allDocuments.push({
            id: docId,
            ...docData
          });
        });
      });
      
      // Sort by createdAt descending (most recent first)
      allDocuments.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });
      
      return allDocuments;
    } catch (error) {
      console.error('Error getting batch documents:', error);
      throw new Error('Failed to get batch documents');
    }
  }

  /**
   * Get paginated collection documents from batches
   */
  static async getCollectionDocumentsPaginated(
    orgId: string,
    collectionId: string,
    options: {
      currentBatch?: number;
      documentsPerPage?: number;
      loadedBatches?: number[];
      currentPage?: number;        // NEW: page number
    } = {}
  ): Promise<PaginatedResult<any>> {
    try {
      const { 
        documentsPerPage = 20, 
        loadedBatches = [], 
        currentPage = 1           // NEW: default to page 1
      } = options;
      
      // Get latest batch number if not provided
      let currentBatch = options.currentBatch;
      if (currentBatch === undefined) {
        const latestBatch = await this.getLatestBatchNumber(orgId, collectionId);
        if (latestBatch === null) {
          return {
            documents: [],
            totalCount: 0,
            hasMore: false,
            currentBatch: 0,
            currentPage: 1,
            totalPages: 0
          };
        }
        currentBatch = latestBatch;
      }
      
      // Calculate which documents we need for this page
      const startIndex = (currentPage - 1) * documentsPerPage;
      const endIndex = startIndex + documentsPerPage;
      
      // Determine which batches to load
      let batchesToLoad = [...loadedBatches];
      if (!batchesToLoad.includes(currentBatch)) {
        batchesToLoad.push(currentBatch);
      }
      
      // Load documents until we have enough for the requested page
      let allDocuments: any[] = [];
      let batchToCheck = currentBatch;
      
      while (allDocuments.length < endIndex && batchToCheck >= 1) {
        if (!batchesToLoad.includes(batchToCheck)) {
          batchesToLoad.push(batchToCheck);
        }
        batchToCheck--;
      }
      
      // Check cache for existing batches
      const { cached, uncachedBatches } = BatchCacheService.getBatchDocumentsWithCache(
        orgId, 
        collectionId, 
        batchesToLoad
      );
      
      console.log(`Cache hit for ${batchesToLoad.length - uncachedBatches.length}/${batchesToLoad.length} batches`);
      console.log(`Loading ${uncachedBatches.length} uncached batches:`, uncachedBatches);
      
      let freshDocuments: any[] = [];
      
      // Only fetch uncached batches
      if (uncachedBatches.length > 0) {
        freshDocuments = await this.getBatchDocuments(orgId, collectionId, uncachedBatches);
        
        // Cache the newly fetched documents by batch
        const documentsByBatch = this.groupDocumentsByBatch(freshDocuments);
        uncachedBatches.forEach(batchNumber => {
          const batchDocs = documentsByBatch[batchNumber] || [];
          BatchCacheService.setCachedBatch(orgId, collectionId, batchNumber, batchDocs);
        });
      }
      
      // Combine cached and fresh documents
      allDocuments = [...cached, ...freshDocuments];
      
      // Sort by creation date (newest first)
      allDocuments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      // Apply pagination to get just the documents for this page
      const paginatedDocuments = allDocuments.slice(startIndex, endIndex);
      
      // Calculate pagination metadata
      const totalPages = Math.ceil(allDocuments.length / documentsPerPage);
      const hasMorePages = currentPage < totalPages;
      const hasMoreBatches = batchToCheck >= 1;
      const hasMore = hasMorePages || hasMoreBatches;
      
      return {
        documents: paginatedDocuments,
        totalCount: allDocuments.length,
        hasMore,
        currentBatch,
        currentPage,
        totalPages,
        loadedBatches: batchesToLoad,
        nextBatch: hasMoreBatches ? batchToCheck : undefined
      };
    } catch (error) {
      console.error('Error getting paginated documents:', error);
      throw new Error('Failed to get paginated documents');
    }
  }

  /**
   * Load more documents (previous batch)
   */
  static async loadMoreDocuments(
    orgId: string,
    collectionId: string,
    currentResult: PaginatedResult<any>
  ): Promise<PaginatedResult<any>> {
    if (!currentResult.hasMore || !currentResult.nextBatch) {
      return currentResult;
    }
    
    try {
      // Get currently loaded batch numbers
      const loadedBatches = [currentResult.currentBatch];
      
      // Add the next batch to load
      const nextBatchToLoad = currentResult.nextBatch;
      loadedBatches.push(nextBatchToLoad);
      
      // Load additional batch
      const newDocuments = await this.getBatchDocuments(
        orgId, 
        collectionId, 
        [nextBatchToLoad]
      );
      
      // Combine with existing documents and sort
      const allDocuments = [...currentResult.documents, ...newDocuments];
      allDocuments.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });
      
      // Check if there are more batches
      const hasMore = nextBatchToLoad > 1;
      const nextBatch = hasMore ? nextBatchToLoad - 1 : undefined;
      
      return {
        documents: allDocuments,
        totalCount: allDocuments.length,
        hasMore,
        currentBatch: currentResult.currentBatch,
        nextBatch
      };
    } catch (error) {
      console.error('Error loading more documents:', error);
      throw new Error('Failed to load more documents');
    }
  }

  /**
   * Get a single document by ID (searches through batches)
   */
  static async getDocument(
    orgId: string,
    collectionId: string,
    documentId: string
  ): Promise<any | null> {
    try {
      // Construct the collection path: orgId_collectionId
      const fullCollectionId = `${orgId}_${collectionId}`;
      console.log(`🔥 Firestore READ: collection "${fullCollectionId}", document "${documentId}"`);
      const docRef = doc(FIREBASE_FIRESTORE, fullCollectionId, documentId);
      const docSnapshot = await getDoc(docRef);
      
      if (docSnapshot.exists()) {
        return {
          id: docSnapshot.id,
          ...docSnapshot.data()
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting document:', error);
      throw new Error('Failed to get document');
    }
  }

  /**
   * Search documents using server-side search (no longer downloads all data to client)
   */
  static async searchDocuments(
    orgId: string,
    collectionId: string,
    searchTerm: string,
    options: {
      searchFields?: string[];
      maxResults?: number;
    } = {}
  ): Promise<SearchResult> {
    try {
      const { searchFields = [], maxResults = 100 } = options;
      
      console.log('☁️ Cloud Function CALL: "searchDocuments"', { orgId, collectionId, searchTerm, searchFields, maxResults });
      const result = await searchDocumentsFn({
        orgId,
        collectionId,
        searchTerm,
        searchFields,
        maxResults
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to search documents');
      }

      return {
        documents: response.data || [],
        totalResults: response.data?.length || 0,
        searchTerm: response.searchTerm || searchTerm
      };
    } catch (error: any) {
      console.error('Error searching documents:', error);
      throw new Error(error.message || 'Failed to search documents');
    }
  }

  // ========== ORGANIZATION OPERATIONS ==========

  /**
   * Get a single organization by ID from Firestore
   */
  static async getOrganization(orgId: string): Promise<Organization | null> {
    try {
      console.log(`🔥 Firestore READ: collection "organizations", document "${orgId}"`);
      const orgDoc = doc(FIREBASE_FIRESTORE, 'organizations', orgId);
      const docSnapshot = await getDoc(orgDoc);
      
      if (docSnapshot.exists()) {
        return { id: docSnapshot.id, ...docSnapshot.data() } as Organization;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching organization:', error);
      throw new Error('Failed to fetch organization');
    }
  }

  // ========== WRITE OPERATIONS (Cloud Functions) ==========

  /**
   * Create a new document via Cloud Function
   */
  static async createDocument(
    orgId: string,
    collectionId: string,
    documentId: string,
    data: any
  ): Promise<CloudFunctionResponse> {
    try {
      console.log('☁️ Cloud Function CALL: "firebaseWrite"', { orgId, collectionId, documentId, data });
      const result = await firebaseWriteFn({
        orgId,
        collectionId,
        documentId,
        data // ✅ Let backend handle timestamps with FieldValue.serverTimestamp()
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create document');
      }

      return response;
    } catch (error: any) {
      console.error('Error creating document:', error);
      throw new Error(error.message || 'Failed to create document');
    }
  }

  /**
   * Update a document via Cloud Function
   */
  static async updateDocument(
    orgId: string,
    collectionId: string,
    documentId: string,
    data: any
  ): Promise<CloudFunctionResponse> {
    try {
      console.log('☁️ Cloud Function CALL: "firebaseUpdate"', { orgId, collectionId, documentId, data });
      const result = await firebaseUpdateFn({
        orgId,
        collectionId,
        documentId,
        data // ✅ Let backend handle timestamps with FieldValue.serverTimestamp()
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update document');
      }

      return response;
    } catch (error: any) {
      console.error('Error updating document:', error);
      throw new Error(error.message || 'Failed to update document');
    }
  }

  /**
   * Delete a document via Cloud Function
   */
  static async deleteDocument(
    orgId: string,
    collectionId: string,
    documentId: string
  ): Promise<CloudFunctionResponse> {
    try {
      console.log('☁️ Cloud Function CALL: "firebaseDelete"', { orgId, collectionId, documentId });
      const result = await firebaseDeleteFn({
        orgId,
        collectionId,
        documentId
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete document');
      }

      return response;
    } catch (error: any) {
      console.error('Error deleting document:', error);
      throw new Error(error.message || 'Failed to delete document');
    }
  }

  // ========== SCHEMA OPERATIONS ==========

  /**
   * Get collection schema via Cloud Function
   */
  static async getCollectionSchema(
    orgId: string,
    collectionId: string
  ): Promise<CollectionSchema | null> {
    try {
      console.log('☁️ Cloud Function CALL: "getCollectionSchema"', { orgId, collectionId });
      const result = await getCollectionSchemaFn({
        orgId,
        collectionId
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to get collection schema');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error getting collection schema:', error);
      throw new Error(error.message || 'Failed to get collection schema');
    }
  }

  /**
   * Validate document against schema via Cloud Function
   */
  static async validateDocumentAgainstSchema(
    orgId: string,
    collectionId: string,
    document: any
  ): Promise<ValidationResult> {
    try {
      console.log('☁️ Cloud Function CALL: "validateDocumentAgainstSchema"', { orgId, collectionId, document });
      const result = await validateDocumentAgainstSchemaFn({
        orgId,
        collectionId,
        document
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to validate document');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error validating document:', error);
      throw new Error(error.message || 'Failed to validate document');
    }
  }

  // ========== PERMISSION OPERATIONS ==========

  /**
   * Get user permissions from localStorage
   */
  static async getUserPermissions(userEmail?: string): Promise<UserPermissions | null> {
    try {
      // If no userEmail provided, we can't get permissions from localStorage
      if (!userEmail) {
        console.warn('No userEmail provided for getUserPermissions');
        return null;
      }

      const key = `permissions_${userEmail}_permissions`;
      const storedPermissions = localStorage.getItem(key);
      
      if (storedPermissions) {
        const permissions = JSON.parse(storedPermissions);
        return {
          permissions: permissions.permissions || [],
          orgPermissions: permissions.orgPermissions || []
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user permissions from localStorage:', error);
      return null;
    }
  }

  /**
   * Add or remove user permission for an organization
   */
  static async setOrganizationPermission(
    orgId: string,
    role: string,
    userEmail: string,
    action: 'add' | 'remove'
  ): Promise<CloudFunctionResponse> {
    try {
      console.log('☁️ Cloud Function CALL: "setOrganizationPermissions"', { orgId, role, userEmail, action });
      const result = await setOrganizationPermissionsFn({
        orgId,
        role,
        userEmail,
        action
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to set organization permission');
      }

      return response;
    } catch (error: any) {
      console.error('Error setting organization permission:', error);
      throw new Error(error.message || 'Failed to set organization permission');
    }
  }

  /**
   * Get organization permissions (who has what roles)
   */
  static async getOrganizationPermissions(orgId: string): Promise<Record<string, string[]> | null> {
    try {
      console.log('☁️ Cloud Function CALL: "getOrganizationPermissions"', { orgId });
      const result = await getOrganizationPermissionsFn({
        orgId
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to get organization permissions');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error getting organization permissions:', error);
      throw new Error(error.message || 'Failed to get organization permissions');
    }
  }

  // ========== API TOKEN OPERATIONS ==========

  /**
   * Create or refresh API token for an organization
   */
  static async createApiToken(
    orgId: string,
    expirationDays?: number
  ): Promise<CloudFunctionResponse> {
    try {
      console.log('☁️ Cloud Function CALL: "createApiToken"', { orgId, expirationDays: expirationDays || 365 });
      const result = await createApiTokenFn({
        orgId,
        expirationDays: expirationDays || 365
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create API token');
      }

      return response;
    } catch (error: any) {
      console.error('Error creating API token:', error);
      throw new Error(error.message || 'Failed to create API token');
    }
  }

  // ========== UTILITY FUNCTIONS ==========

  /**
   * Generate a unique document ID
   */
  static generateDocumentId(): string {
    return `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create default form data from schema
   */
  static createDefaultFormData(schema: CollectionSchema): Record<string, any> {
    const defaultData: Record<string, any> = {};

    for (const [fieldName, fieldSchema] of Object.entries(schema.fields)) {
      defaultData[fieldName] = this.getDefaultValueForField(fieldSchema);
    }

    return defaultData;
  }

  /**
   * Get default value for a schema field
   */
  static getDefaultValueForField(fieldSchema: SchemaField): any {
    switch (fieldSchema.type) {
      case 'string':
        return '';
      case 'number':
        return 0;
      case 'boolean':
        return false;
      case 'date':
        return new Date().toISOString().split('T')[0];
      case 'array':
        return [];
      case 'object':
        if (fieldSchema.objectSchema) {
          const defaultObject: Record<string, any> = {};
          for (const [subFieldName, subFieldSchema] of Object.entries(fieldSchema.objectSchema)) {
            defaultObject[subFieldName] = this.getDefaultValueForField(subFieldSchema);
          }
          return defaultObject;
        }
        return {};
      case 'reference':
        return '';
      default:
        return null;
    }
  }

  /**
   * Check if user has specific permission for an organization
   */
  static async hasPermission(
    userEmail: string,
    orgId: string,
    permission: 'read' | 'write' | 'admin' | 'api'
  ): Promise<boolean> {
    try {
      const permissions = await this.getUserPermissions(userEmail);
      
      if (!permissions) return false;
      
      // Check if user is APP_ADMIN
      if (permissions.permissions.includes('APP_ADMIN')) return true;
      
      // Check for org-specific permission
      return permissions.orgPermissions.includes(`${orgId}_${permission}`);
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  }

  /**
   * Check if user is APP_ADMIN
   */
  static async isAppAdmin(userEmail: string): Promise<boolean> {
    try {
      const permissions = await this.getUserPermissions(userEmail);
      return permissions?.permissions?.includes('APP_ADMIN') || false;
    } catch (error) {
      console.error('Error checking APP_ADMIN status:', error);
      return false;
    }
  }

  /**
   * Get user's organizations (from their permissions)
   */
  static async getUserOrganizations(userEmail: string): Promise<string[]> {
    try {
      const permissions = await this.getUserPermissions(userEmail);
      
      if (!permissions) return [];
      
      const orgIds = new Set<string>();
      
      // Extract organization IDs from orgPermissions
      permissions.orgPermissions.forEach(permission => {
        if (permission.includes('_')) {
          const orgId = permission.split('_')[0];
          orgIds.add(orgId);
        }
      });
      
      return Array.from(orgIds);
    } catch (error) {
      console.error('Error getting user organizations:', error);
      return [];
    }
  }

  private static groupDocumentsByBatch(documents: any[]): Record<number, any[]> {
    return documents.reduce((acc, doc) => {
      const batch = doc.batch || 1;
      if (!acc[batch]) {
        acc[batch] = [];
      }
      acc[batch].push(doc);
      return acc;
    }, {} as Record<number, any[]>);
  }
  
  // Also add this method for clearing cache when data is modified:
  static clearCollectionCache(orgId: string, collectionId: string): void {
    BatchCacheService.clearCache(orgId, collectionId);
  }
}